import { Button } from '@base/components/ui/button';
import { Slider } from '@base/components/ui/slider';
import { useIsMobile } from '@base/hooks/use-mobile';
import { formatTime } from '@datetime';
import {
  LoaderIcon,
  PauseIcon,
  PlayIcon,
  SkipBackwardIcon,
  SkipForwardIcon,
  VolumeIcon
} from '@icons';
import { MediaitemFileRole, MediaitemModel } from '@models';
import { setupForDark } from '@theme';
import { useNetworkState } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import MediaitemAccessError, { MediaitemErrorCode } from './MediaitemAccessError';

const MediaitemAudioPlayer = ({ mediaitem }: { mediaitem: MediaitemModel }) => {
  const files = useMemo(() => {
    if (!mediaitem?.files) {
      return {};
    }
    return {
      original: mediaitem.files.find((file) => file.role === MediaitemFileRole.ORIGINAL),
      displayMP3: mediaitem.files.find(
        (file) =>
          file.role === MediaitemFileRole.DISPLAY && file.contentType === 'audio/mpeg' && file.ready
      )
    };
  }, [mediaitem?.files]);

  const src = files.displayMP3?.url || files.original?.url;
  const network = useNetworkState();
  const isMobile = useIsMobile(false);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [accessError, setAccessError] = useState({
    hasAccessError: false,
    errorCode: undefined
  });
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const [waveformReady, setWaveformReady] = useState(false);

  useEffect(() => {
    setAccessError({
      hasAccessError: false,
      errorCode: undefined
    });
  }, [mediaitem]);

  useEffect(() => {
    // Handle network state
    if (!network.online) {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_NETWORK
      });
    } else if (
      accessError.hasAccessError &&
      accessError.errorCode === MediaitemErrorCode.MEDIA_ERR_NETWORK
    ) {
      // Clear the network error when coming back online
      setAccessError({
        hasAccessError: false,
        errorCode: undefined
      });
    }

    // Handle audio element errors
    if (audioRef.current) {
      const handleError = (e: Event) => {
        const audio = e.target as HTMLAudioElement;
        // Only set the error if it's not already a network error
        if (
          audio.error &&
          (!accessError.hasAccessError ||
            accessError.errorCode !== MediaitemErrorCode.MEDIA_ERR_NETWORK)
        ) {
          setAccessError({
            hasAccessError: true,
            errorCode: audio.error.code
          });
        }
      };

      audioRef.current.addEventListener('error', handleError);
      return () => {
        audioRef.current?.removeEventListener('error', handleError);
      };
    }
  }, [network.online, audioRef, accessError]);

  useEffect(() => {
    // Wait for isMobile to be determined before creating WaveSurfer (expensive operation)
    if (waveformRef.current && src && isMobile !== undefined) {
      setWaveformReady(false);
      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#ffffff',
        progressColor: '#aaaaaa',
        height: isMobile ? 100 : 200,
        barWidth: 2,
        barHeight: 1,
        barGap: 2,
        media: audioRef.current
      });

      wavesurfer.on('ready', () => {
        setWaveformReady(true);
        if (audioRef.current) {
          audioRef.current.play();
          setPlaying(true);
        }
      });

      return () => wavesurfer.destroy();
    }
  }, [src, waveformRef?.current, isMobile]);

  const togglePlay = async () => {
    if (audioRef.current && waveformReady) {
      try {
        if (playing) {
          audioRef.current.pause();
        } else {
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
        }
        setPlaying(!playing);
      } catch (error) {
        // Handle any play/pause errors gracefully
        console.error('Playback error:', error);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handlePlay = () => {
    setPlaying(true);
  };

  const handlePause = () => {
    setPlaying(false);
  };

  const handleVolumeChange = (value: number[]) => {
    if (audioRef.current) {
      const newVolume = value[0];
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
      setMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      const newMutedState = !muted;
      audioRef.current.muted = newMutedState;
      setMuted(newMutedState);
      if (newMutedState) {
        audioRef.current.volume = 0;
        setVolume(0);
      } else {
        audioRef.current.volume = volume > 0 ? volume : 1;
        setVolume(volume > 0 ? volume : 1);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(event.target as Node)) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (accessError.hasAccessError) {
    return (
      <MediaitemAccessError
        type='audio'
        errorCode={accessError.errorCode}
        className='h-full w-full'
      />
    );
  }

  return (
    <div
      className={classNames(
        'w-full h-full flex flex-col justify-center items-center',
        setupForDark('text-primary')
      )}
    >
      <div className='p-sm sm:p-0 h-[120px] sm:h-[200px] w-full relative'>
        {!waveformReady && src && (
          <div className='absolute inset-0 flex items-center justify-center'>
            <LoaderIcon size={48} />
          </div>
        )}
        <div
          ref={waveformRef}
          className={classNames('waveform h-[100px] sm:h-[200px]', { 'opacity-0': !waveformReady })}
        ></div>
        <audio
          ref={audioRef}
          src={src}
          preload='auto'
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          onClick={togglePlay}
          autoPlay={false}
        />
      </div>
      <div className='flex w-full items-center justify-between gap-xxl'>
        <span className={classNames('flex flex-row gap-xxs P6R', setupForDark('text-primary'))}>
          <span>{formatTime(currentTime)}</span>
          <span>/</span>
          <span>{formatTime(duration)}</span>
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 hover:bg-white/20'
            onClick={() => {
              if (audioRef.current && waveformReady) audioRef.current.currentTime -= 10;
            }}
            disabled={!waveformReady}
          >
            <SkipBackwardIcon className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 hover:bg-white/20'
            onClick={togglePlay}
            disabled={!waveformReady}
          >
            {playing ? (
              <PauseIcon className='h-4 w-4 fill-current' />
            ) : (
              <PlayIcon className='h-4 w-4 fill-current' />
            )}
          </Button>

          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 hover:bg-white/20'
            onClick={() => {
              if (audioRef.current && waveformReady) audioRef.current.currentTime += 10;
            }}
            disabled={!waveformReady}
          >
            <SkipForwardIcon className='h-4 w-4' />
          </Button>
        </div>
        <div ref={volumeControlRef} className='relative flex items-center'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 hover:bg-white/20'
            onClick={() => {
              toggleMute();
              setShowVolumeSlider(!showVolumeSlider);
            }}
          >
            {muted || volume === 0 ? (
              <VolumeIcon className='h-4 w-4' />
            ) : (
              <VolumeIcon className='h-4 w-4' />
            )}
          </Button>
          <Slider
            orientation='horizontal'
            className='w-[80px] h-[15px] bg-transparent'
            value={[muted ? 0 : volume]}
            min={0}
            max={1}
            step={0.01}
            onValueChange={handleVolumeChange}
          />
        </div>
      </div>
    </div>
  );
};

export default MediaitemAudioPlayer;
